/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
// Ember imports
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
// External imports
import config from 'linced-app/config/environment';
import { getKeyAndSourceFromUpload } from '@linc-technologies/linced-common/utils/uppy';
import EventType from '@linc-technologies/linced-common/enums/events/event-type';
import EventStatus from '@linc-technologies/linced-common/enums/events/event-status';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import SharingLevel from '@linc-technologies/linced-common/enums/media-sharing-level';
import MediaType from '@linc-technologies/linced-common/enums/media-type';
import Staff from '@linc-technologies/linced-common/enums/roles/staff';
import { hasRole } from 'linced-app/utils/roles';

export default class Event extends Component {
  @action
  setCancelReason(value) {
    this.cancelReason = value;
  }

  @action
  setDeleteCommunications(value) {
    this.deleteCommunications = value;
  }

  // Attrs
  EventStatus = EventStatus;

  EventType = EventType;

  // Services
  @service
  countdownButton;

  @service
  fetch;

  @service
  permissions;

  @service
  roles;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  event = null;
  @tracked
  editDialogOpen = false;
  @tracked
  hideImage = false;
  @tracked
  uploading = false;
  @tracked
  empty = true;
  @tracked
  cancelDialogOpen = false;
  @tracked cancelReason = '';
  @tracked cancelInProgress = false;
  @tracked
  cancelFailed = false;
  @tracked
  currentNextStatus = null;
  @tracked
  deleteCommunications = false;
  @tracked
  deleteDialogOpen = false;
  @tracked
  deleteInProgress = false;
  @tracked
  deleteFailed = false;
  @tracked
  duplicateDialogOpen = false;
  @tracked
  duplicateInProgress = false;
  @tracked
  nextStageDialogOpen = false;
  updateModel() {}

  @computed('event.status')
  get statusClass() {
    switch (this.event.status) {
      case EventStatus.DRAFT:
        return 'event-draft';
      case EventStatus.PENDING_APPROVAL:
        return 'event-pending';
      case EventStatus.APPROVED:
        return 'event-approved';
      case EventStatus.OPEN_TO_CAREGIVERS:
      case EventStatus.OPEN_TO_STAFF:
        return 'event-public';
      case EventStatus.CLOSED:
        return 'event-closed';
      case EventStatus.COMPLETE:
        return 'event-complete';
      case EventStatus.CANCELLED:
        return 'event-cancelled';
    }
  }

  @hasRole(Staff.APPROVE_EVENTS)
  isEventsApprover;

  @hasRole(Staff.PRINCIPAL)
  isPrincipal;

  @tracked
  moveFailed;

  @tracked
  moveInProgress;

  @computed('isEventsApprover', 'isPrincipal')
  get canApproveEvents() {
    return this.isEventsApprover || this.isPrincipal;
  }

  @computed('session.{personId,scopes.[]}', 'event.author.id')
  get canEditEvent() {
    // Administrators can edit any event
    if (this.permissions.meet(Scopes.TENANT_ADMIN)) {
      return true;
    }
    // Other users can edit their own events only
    return this.get('event.author.id') === this.get('session.personId');
  }

  @computed('canEditEvent', 'canApproveEvents', 'event.status')
  get canCancelEvents() {
    return (
      (this.canApproveEvents || this.canEditEvent) &&
      (this.event.status === EventStatus.OPEN_TO_CAREGIVERS ||
        this.event.status === EventStatus.OPEN_TO_STAFF ||
        this.event.status === EventStatus.CLOSED)
    );
  }

  @computed('event.{status,internal,requiresApproval}')
  get nextStatus() {
    switch (this.event.status) {
      case EventStatus.DRAFT:
        if (this.event.requiresApproval) {
          return EventStatus.PENDING_APPROVAL;
        }
        if (this.event.internal) {
          return EventStatus.OPEN_TO_STAFF;
        }
        return EventStatus.OPEN_TO_CAREGIVERS;
      case EventStatus.PENDING_APPROVAL:
        return EventStatus.APPROVED;
      case EventStatus.APPROVED:
        if (this.event.internal) {
          return EventStatus.OPEN_TO_STAFF;
        }
        return EventStatus.OPEN_TO_CAREGIVERS;
      case EventStatus.OPEN_TO_CAREGIVERS:
        return EventStatus.CLOSED;
      case EventStatus.OPEN_TO_STAFF:
        return EventStatus.COMPLETE;
      case EventStatus.CLOSED:
        return EventStatus.COMPLETE;
      default:
        return null;
    }
  }

  @computed('session.{personId,scopes.[]}', 'event.status')
  get canDeleteEvent() {
    // Don't allow deletion if event is open to caregivers or closed
    if (
      [EventStatus.OPEN_TO_CAREGIVERS, EventStatus.CLOSED].includes(
        this.event.status,
      )
    ) {
      return false;
    }
    // Administrators can delete any event
    if (this.permissions.meet(Scopes.TENANT_ADMIN)) {
      return true;
    }
    // Other users can delete their own events only
    return this.get('event.author.id') === this.get('session.personId');
  }

  get cancelActionDisabled() {
    return this.cancelInProgress || this.cancelReason.length > 50;
  }

  @action
  returnToCalendar() {
    this.router.transitionTo('home.events');
  }

  @action
  editEvent() {
    this.editDialogOpen = true;
  }

  @action
  openDeleteDialog() {
    this.deleteDialogOpen = true;
    this.deleteCommunications = true;
    this.countdownButton.initialise(3);
  }

  @action
  delete() {
    this.deleteInProgress = true;
    this.deleteFailed = false;
    this.event
      .destroyRecord()
      .then(() => {
        if (this.deleteCommunications) {
          this.store
            .query('posts/communitynotice', {
              eventId: this.event.id,
            })
            .then((notices) => {
              notices.forEach((notice) => {
                notice.destroyRecord();
              });
            });
          this.store
            .query('posts/staffnotice', {
              eventId: this.event.id,
            })
            .then((notices) => {
              notices.forEach((notice) => {
                notice.destroyRecord();
              });
            });
        }
        this.toaster.success(`Event deleted successfully`);
      })
      .catch(() => {
        this.deleteFailed = true;
        this.toaster.error(`Error removing the event, please try again`);
      })
      .finally(() => {
        this.deleteInProgress = false;
        this.deleteFailed = false;
        this.router.transitionTo('home.events');
        this.countdownButton.cleanup();
      });
  }

  @action
  duplicate() {
    this.duplicateInProgress = true;
    let url = `${config.APP.apiHost}/events/${config.APP.apiPath}/events/:duplicate`;
    let options = {
      data: JSON.stringify({
        eventId: this.event.id,
      }),
    };
    this.fetch
      .post(url, options)
      .then((response) => {
        this.toaster.success(`Event duplicated successfully`);
        this.router
          .transitionTo('home.events.event', response.event.id)
          .then(() => {
            this.editDialogOpen = true;
          });
      })
      .catch(() => {
        this.toaster.error(`Error duplicating the event, please try again`);
      })
      .finally(() => {
        this.duplicateDialogOpen = false;
        this.duplicateInProgress = false;
      });
  }

  @action
  openCancelDialog() {
    this.cancelDialogOpen = true;
    this.cancelFailed = false;
  }

  @action
  closeCancelDialog() {
    this.cancelDialogOpen = false;
    this.cancelReason = '';
  }

  @action
  cancel() {
    let reason = this.cancelReason ? this.cancelReason.trim() : '';
    this.cancelInProgress = true;

    this.event.setProperties({
      cancellationReason: reason || null,
      status: EventStatus.CANCELLED,
    });

    this.event
      .save()
      .then(() => {
        this.toaster.success(`Event cancelled successfully`);
        this.closeCancelDialog();
      })
      .catch(() => {
        this.cancelFailed = true;
        this.event.rollbackAttributes();
        this.toaster.error(`Error cancelling the event, please try again`);
      })
      .finally(() => {
        this.cancelInProgress = false;
      });
  }

  @action
  openNextStageDialog() {
    this.currentNextStatus = this.nextStatus;
    this.nextStageDialogOpen = true;
  }

  @action
  move() {
    this.moveInProgress = true;
    this.moveFailed = false;
    this.event.set('status', this.nextStatus);

    this.event
      .save()
      .then(() => {
        this.toaster.success(`Event updated successfully`);
      })
      .catch(() => {
        this.moveFailed = true;
        this.event.rollbackAttributes();
        this.toaster.error(`Error updating the event, please try again`);
      })
      .finally(() => {
        this.moveInProgress = false;
        this.nextStageDialogOpen = false;
      });
  }

  @action
  startUpload() {
    this.uploading = true;
    this.empty = false;
  }

  @action
  handleUpload(file, upload) {
    let { key, mediaSource } = getKeyAndSourceFromUpload(upload);
    let event = this.event;

    let sharedWith = SharingLevel.SCHOOL;

    this.store
      .createRecord('media', {
        schoolId: this.get('session.school'),
        personId: this.get('session.person'),
        sharedWith,
        mediaType: MediaType.IMAGE,
        mediaSource: mediaSource,
        mimeType: file.type,
        fileName: file.name,
        storageKey: key,
        fileSize: file.size,
      })
      .save()
      .then((media) => {
        event.set('eventImage', media.id);
        return event.save();
      })
      .catch(() => {
        event.rollbackAttributes();
        this.toaster.error(`Error saving image!`);
      })
      .finally(() => {
        this.uploading = false;
        this.hideImage = false;
        this.empty = true;
      });
  }

  /**
   * handleUploadError provides the callback to deal with upload errors.
   */
  @action
  handleUploadError() {
    this.uploading = false;
    this.toaster.error(`Error saving event image!`);
  }

  @action
  revertImage() {
    this.event.set('eventImage', '');
    this.hideImage = false;
    this.event
      .save()
      .then(() => {
        this.toaster.success(`Image reverted successfully`);
      })
      .catch(() => {
        this.event.rollbackAttributes();
        this.toaster.error(`Image revert failed, please try again`);
      });
  }
}
